import {
  toArray,
  getMimeBase,
  isAudio,
  isImage,
  isText,
  isSVG,
  isVideo,
} from '@nx-superprep/utils';

import { Resource } from './resource.model';

export const enum ChoiceType {
  wrong = 'wrong',
  answer = 'answer',
  question = 'question',
}

export class Choice {
  name!: string
  resources!: Resource[]
  type!: ChoiceType
  mimeType!: string // mimeType of the main resource
  isActive!: boolean // audio animation is playing
  animationState!: boolean // answer animation state

  constructor(options: Partial<Choice>) {
    if (!options) { return; }
    this.type = options.type ?? ChoiceType.wrong
    this.name = options.name ?? ''
    this.resources = toArray(options.resources ?? []).map((r) => new Resource(r))
    this.mimeType = options.mimeType ?? ''
    this.isActive = false
    this.animationState = false
  }

  get isAssetized(): boolean {
    return this.resources.every((r) => r.isAssetized)
  }

  get isValid(): boolean {
    switch (this.mimeBase) {
      case 'video':
        return this.hasVideo
      case 'audio':
        return this.hasAudio
      case 'image':
        return this.hasImage
      case 'text':
        return this.hasText
      default:
        return true
    }
  }

  get isQuestion(): boolean {
    return this.type === ChoiceType.question
  }
  get isAnswer(): boolean {
    return this.type === ChoiceType.answer
  }
  get isChoice(): boolean {
    return !this.isQuestion
  }

  get isWrong(): boolean {
    return this.type === ChoiceType.wrong
  }
  get isRight(): boolean {
    return !this.isWrong
  }

  get mimeBase(): string {
    return getMimeBase(this.mimeType)
  }
  get isVideo(): boolean {
    return isVideo(this.mimeType)
  }
  get isAudio(): boolean {
    return isAudio(this.mimeType)
  }
  get isImage(): boolean {
    return isImage(this.mimeType)
  }
  get isSVG(): boolean {
    return isSVG(this.mimeType)
  }
  get isText(): boolean {
    return isText(this.mimeType)
  }
  get isElement(): boolean {
    return this.isText && this.hasElement
  }
  get isHTML(): boolean {
    return this.isSVG && this.hasHTML
  }

  get hasVideo(): boolean {
    return this.videoUrl?.length > 0
  }
  get hasAudio(): boolean {
    return this.audioUrl?.length > 0
  }
  get hasImage(): boolean {
    return this.imageUrl?.length > 0
  }
  get hasText(): boolean {
    return this.textUrl?.length > 0
  }
  get hasElement(): boolean {
    return this.elementUrl?.length > 0
  }
  get hasHTML(): boolean {
    return Boolean(this.html)
  }

  get duration(): number {
    return Number(this.audio?.duration) || 0
  }
  set duration(value) {
    if (this.audio) { this.audio.duration = value }
  }

  get videoUrl(): string {
    return String(this.video?.url ?? '')
  }
  get audioUrl(): string {
    return String(this.audio?.url ?? '')
  }
  get imageUrl(): string {
    return String(this.image?.url ?? '')
  }
  get textUrl(): string {
    return String((this.text?.name.length
      ? this.text.name
      : this.name.length
        ? this.name
        : undefined
    ) ?? '')
  }
  get elementUrl(): string {
    return String(this.element?.name ?? '')
  }

  get video() {
    return this.resources.find((r) => r.isVideo)
  }
  get audio() {
    return this.resources.find((r) => r.isAudio)
  }
  get image() {
    return this.resources.find((r) => r.isImage)
  }
  get svg() {
    return this.resources.find((r) => r.isSVG)
  }
  get text() {
    return this.resources.find((r) => r.isText)
  }
  get element() {
    return this.resources.find((r) => r.isElement)
  }
  get html() {
    return this.resources.find((r) => r.isHTML)
  }

  isLongText(text: string) {
    return text?.length > 29
  }
  // text choice has multiple parts
  isCompositeChoice(choice: Choice) {
    return choice.textUrl?.indexOf(';') > 0
  }
  // text choice length
  isLongChoice(choice: Choice) {
    return this.isLongText(choice.textUrl)
  }
}
